// 🧾 Footer - Pied de page du site
// Affiche logo, navigation, infos pratiques, contact et crédits
// Utilisé dans : src/App.jsx (sur toutes les pages publiques)

import { Link } from 'react-router-dom'
import { Mail, MapPin, Calendar, Facebook, Instagram, Music } from 'lucide-react'
import globalData from '@/locales/global.json'
import homeData from '@/locales/home.json'

/**
 * Pied de page complet du site Rock'In Agde
 * Organisé en 4 colonnes (responsive) : Logo, Navigation, Infos, Contact
 * Inclut les liens légaux et crédits de conception
 */
export default function Footer() {
  const { site, navigation, footer, contact } = globalData
  const { stats } = homeData

  return (
    <footer className="bg-gradient-to-br from-neutral-900 via-neutral-950 to-black text-neutral-200">
      <div className="container-custom px-4 pt-12 pb-6">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Logo & Description */}
          <div>
            <img 
              src="/images/logos/logo-vertical-white.png" 
              alt="Rock'In Agde" 
              className="h-16 w-auto object-contain mb-4"
            />
            <p className="text-sm text-neutral-400 leading-relaxed">
              {site.baseline}
            </p>
          </div>
          
          {/* Navigation */}
          <div>
            <h4 className="font-bold text-white mb-4 text-sm uppercase tracking-wider">{footer.sections.navigation.title}</h4>
            <div className="space-y-2 text-sm">
              {navigation.desktop.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  className="block text-neutral-400 hover:text-primary-400 transition"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
          
          {/* Infos pratiques */}
          <div>
            <h4 className="font-bold text-white mb-4 text-sm uppercase tracking-wider">{footer.sections.nextEdition.title}</h4>
            <div className="space-y-3 text-sm">
              <p className="flex items-center gap-2 text-neutral-400">
                <Calendar size={16} className="text-primary-400" />
                <span>{stats.nextEdition}</span>
              </p>
              <p className="flex items-start gap-2 text-neutral-400">
                <MapPin size={16} className="mt-0.5 flex-shrink-0 text-primary-400" />
                <span>
                  {footer.sections.nextEdition.location.line1}<br />
                  {footer.sections.nextEdition.location.line2}
                </span>
              </p>
            </div>
          </div>
          
          {/* Contact & Réseaux */}
          <div>
            <h4 className="font-bold text-white mb-4 text-sm uppercase tracking-wider">{footer.sections.contact.title}</h4>
            <div className="space-y-3 text-sm">
              <p className="flex items-center gap-2">
                <Mail size={16} className="text-primary-400" />
                <a
                  href={`mailto:${contact.email}`}
                  className="text-neutral-400 hover:text-primary-400 transition break-all"
                >
                  {contact.email}
                </a>
              </p>
              <div className="flex gap-3 pt-2">
                <a
                  href={contact.socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-neutral-800 hover:bg-primary-500 rounded-lg transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook size={18} />
                </a>
                <a
                  href={contact.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-neutral-800 hover:bg-primary-500 rounded-lg transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
        
        {/* Copyright & Legal */}
        <div className="border-t border-neutral-800 pt-6 text-center text-sm text-neutral-500">
          <p className="mb-2">
            {footer.copyright.replace('{year}', site.copyright)}
          </p>
          <p className="space-x-3">
            <Link to="/legal" className="hover:text-primary-400 transition">
              {footer.legal.mentionsLegales}
            </Link>
            <span>•</span>
            <Link to="/privacy-policy" className="hover:text-primary-400 transition">
              {footer.legal.politiqueConfidentialite}
            </Link>
          </p>
        </div>
      </div>
      
      {/* Credits bar */}
      <div className="bg-black py-3">
        <div className="container-custom px-4">
          <p className="text-center text-xs text-neutral-600">
            Site propulsé par <span className="font-semibold text-neutral-500">DOMELVIA™</span> en collaboration avec <span className="font-semibold text-neutral-500">Avana&Co</span>
          </p>
        </div>
      </div>
    </footer>
  )
}
